import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Collapse, Dropdown, Form, InputGroup, Modal, Nav, Spinner } from 'react-bootstrap';
import { Check2, Clipboard, Download, Pencil, Search } from 'react-bootstrap-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Client,
  getDefaultClientConfigValues,
  ACCOUNT_ORG_VALUES,
  ACCOUNT_ORG_DISPLAY,
  getAccountOrgBadgeInfo,
} from '@/types';
import { CONTRACT_DISPLAY, CONTRACT_VALUES, partnerNames, resolveContract, trialExpired } from '@/config/contract';
import type { AccountOrgValue } from '@/types';
import { clientService, groupClientsByType } from '@/services/clientService';
import { accountOrgService } from '@/services/accountOrgService';
import { BulkInactiveDialog, type BulkInactiveAction } from '@/components/clientFlags/BulkInactiveDialog';
import { ClientFlagControl } from '@/components/clientFlags/ClientFlagControl';
import { ClientTags } from '@/components/clientFlags/ClientTags';
import { OrganisationReview } from '@/components/config/OrganisationReview';
import { ClientTableGroup } from '@/components/ClientTableGroup';
import { CopyButton } from '@/components/ui/CopyButton';
import { PageHeader } from '@/components/ui/PageHeader';
import { useClientFlags } from '@/hooks/useClientFlags';
import { FileExportService } from '@/utils/fileExport';
import { formatNzDate, formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import { listAllRecentDeployments } from '@/services/deploymentService';
import './Configs.css';

/** Which clients the list holds: the ones in use, the ones marked inactive, or both. */
type Scope = 'active' | 'inactive' | 'all';

/** A button that copies a block of JSON and says so. */
function CopyJsonButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  return (
    <Button size="sm" variant="outline-secondary" onClick={copy}>
      {copied ? <Check2 className="me-1" /> : <Clipboard className="me-1" />}
      {copied ? 'Copied' : label}
    </Button>
  );
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

export default function Configs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Each client's saved record and its two tags. The page shows them and never writes them by itself.
  const flags = useClientFlags();
  const metadataMap = flags.metadata;
  const [scope, setScope] = useState<Scope>('active');
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<BulkInactiveAction | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [exporting, setExporting] = useState(false);
  const [showDates, setShowDates] = useState(false);
  // A contract, "partner:<name>", "expired" (a trial past its end date) or "unset".
  const [contractFilter, setContractFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [trialStartFrom, setTrialStartFrom] = useState('');
  const [trialStartTo, setTrialStartTo] = useState('');
  const [trialEndFrom, setTrialEndFrom] = useState('');
  const [trialEndTo, setTrialEndTo] = useState('');
  const [orgMap, setOrgMap] = useState<Map<string, AccountOrgValue | null>>(new Map());

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await clientService.getAllClients();
        setClients(data);

        // Ask AWS which organisation each account is in. This only reads. Where the answer
        // differs from the saved record, the page says so and a person saves it (OrganisationReview).
        // The page must still work when the lookup fails.
        void accountOrgService
          .classifyAll(data)
          .then(setOrgMap)
          .catch((err) => console.error('Account organisation lookup failed:', err));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'The clients did not load. Reload the page to try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const requested = searchParams.get('client');
  const selectClient = (client: Client) => setSearchParams({ client: client.name }, { replace: true });
  const editClient = (client: Client) =>
    navigate(`/tools/update-client-config?clientName=${encodeURIComponent(client.name)}`);

  // A link to an inactive client must still show it in the list. The list starts on active
  // clients, so it widens to all of them once, when the tags say the linked client is inactive.
  const widenedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!requested || widenedFor.current === requested || flags.status !== 'ready') return;
    widenedFor.current = requested;
    if (flags.isInactive(requested)) setScope((current) => (current === 'active' ? 'all' : current));
  }, [requested, flags]);

  const scopeCounts = useMemo(() => {
    const inactive = clients.filter((c) => flags.isInactive(c.name)).length;
    return { active: clients.length - inactive, inactive, all: clients.length };
  }, [clients, flags]);

  const dateFilterCount = (trialStartFrom || trialStartTo ? 1 : 0) + (trialEndFrom || trialEndTo ? 1 : 0);
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (contractFilter) count++;
    if (orgFilter) count++;
    if (trialStartFrom || trialStartTo) count++;
    if (trialEndFrom || trialEndTo) count++;
    return count;
  }, [contractFilter, orgFilter, trialStartFrom, trialStartTo, trialEndFrom, trialEndTo]);

  const clearFilters = () => {
    setContractFilter('');
    setOrgFilter('');
    setTrialStartFrom('');
    setTrialStartTo('');
    setTrialEndFrom('');
    setTrialEndTo('');
  };

  // Every client the search and the filters match, whichever of active and inactive it is.
  const matching = useMemo(() => {
    let result = clients;

    // Text search (existing)
    const term = search.toLowerCase().trim();
    if (term) {
      result = result.filter((c) => c.name.toLowerCase().includes(term));
    }

    // Account org filter
    if (orgFilter) {
      result = result.filter((c) => {
        const org = orgMap.get(c.name) ?? metadataMap.get(c.name)?.accountOrg ?? null;
        if (orgFilter === 'unknown') return org === null;
        return org === orgFilter;
      });
    }

    // Contract filter
    if (contractFilter) {
      result = result.filter((c) => {
        const meta = metadataMap.get(c.name);
        const contract = resolveContract(meta, c.config);
        if (contractFilter === 'unset') return !contract;
        if (contractFilter === 'expired') return contract === 'trial' && trialExpired(meta);
        if (contractFilter.startsWith('partner:')) {
          return contract === 'partner' && meta?.partner === contractFilter.slice('partner:'.length);
        }
        return contract === contractFilter;
      });
    }

    // Trial start date range
    if (trialStartFrom) {
      result = result.filter((c) => {
        const meta = metadataMap.get(c.name);
        return meta?.trialStartDate && meta.trialStartDate >= trialStartFrom;
      });
    }
    if (trialStartTo) {
      result = result.filter((c) => {
        const meta = metadataMap.get(c.name);
        return meta?.trialStartDate && meta.trialStartDate <= trialStartTo;
      });
    }

    // Trial end date range
    if (trialEndFrom) {
      result = result.filter((c) => {
        const meta = metadataMap.get(c.name);
        return meta?.trialEndDate && meta.trialEndDate >= trialEndFrom;
      });
    }
    if (trialEndTo) {
      result = result.filter((c) => {
        const meta = metadataMap.get(c.name);
        return meta?.trialEndDate && meta.trialEndDate <= trialEndTo;
      });
    }

    return result;
  }, [
    search,
    clients,
    contractFilter,
    orgFilter,
    trialStartFrom,
    trialStartTo,
    trialEndFrom,
    trialEndTo,
    metadataMap,
    orgMap,
  ]);

  // The list: the matches that are in the chosen part (active, inactive or all).
  const filtered = useMemo(
    () => (scope === 'all' ? matching : matching.filter((c) => flags.isInactive(c.name) === (scope === 'inactive'))),
    [matching, scope, flags]
  );
  // Matches the chosen part leaves out. An empty list says so, and offers to search every client.
  const hiddenMatches = matching.length - filtered.length;

  // The open client lives in the address, so a client's config can be linked to. With no client
  // named there, the first one in the list opens.
  const selected = useMemo<Client | null>(() => {
    if (clients.length === 0) return null;
    const match = requested ? clients.find((c) => c.name === requested) : undefined;
    if (match) return match;
    const listed = groupClientsByType(filtered);
    if (listed.productionClients[0] || listed.devClients[0]) return listed.productionClients[0] ?? listed.devClients[0];
    const { productionClients, devClients } = groupClientsByType(clients);
    return productionClients[0] ?? devClients[0] ?? null;
  }, [clients, filtered, requested]);
  const requestedMissing = Boolean(requested) && clients.length > 0 && selected?.name !== requested;

  // ── Ticked rows: a change to many clients at once. Only a row in the list counts. ──
  const tickedListed = useMemo(
    () => filtered.map((c) => c.name).filter((name) => ticked.has(name)),
    [filtered, ticked]
  );
  const tickedActive = tickedListed.filter((name) => !flags.isInactive(name));
  const tickedInactive = tickedListed.filter((name) => flags.isInactive(name));
  const changeTicks = (names: string[], on: boolean) =>
    setTicked((current) => {
      const next = new Set(current);
      for (const name of names) {
        if (on) next.add(name);
        else next.delete(name);
      }
      return next;
    });

  // Helper function to merge config with defaults
  const mergeConfigWithDefaults = (config: any): any => {
    const defaults = getDefaultClientConfigValues();
    return {
      ...defaults,
      ...Object.fromEntries(Object.entries(config).filter(([_, v]) => v !== undefined && v !== null && v !== '')),
    };
  };

  // Unified export function
  const performExport = async (format: 'csv' | 'json', withDefaults: boolean) => {
    setExporting(true);
    setError(null);
    try {
      // Fetch deployment data
      const deployments = await listAllRecentDeployments(1000).catch(() => []);

      // Group deployments by client
      const deploymentsByClient = new Map();
      deployments.forEach((deploy) => {
        if (!deploy.clientName) return;
        const list = deploymentsByClient.get(deploy.clientName) || [];
        list.push(deploy);
        deploymentsByClient.set(deploy.clientName, list);
      });

      // Enrich clients with deployment data and optionally merge with defaults
      const enrichedClients = filtered.map((client) => {
        const clientDeployments = deploymentsByClient.get(client.name) || [];
        const lastDeploy = clientDeployments[0]; // Already sorted by most recent

        const config = withDefaults ? mergeConfigWithDefaults(client.config) : client.config;

        return {
          ...client,
          config,
          lastDeployment: lastDeploy
            ? {
                timestamp: lastDeploy.startedAt || lastDeploy.startTime || '',
                imageTag: lastDeploy.imageTag || '',
                status:
                  lastDeploy.status === 'success' || lastDeploy.status === 'failed' || lastDeploy.status === 'running'
                    ? lastDeploy.status
                    : 'running',
                deploymentId: lastDeploy.deploymentId,
              }
            : client.lastDeployment,
          deploymentCount: clientDeployments.length,
        };
      });

      if (format === 'csv') {
        // Prepare CSV data
        const exportData = enrichedClients.map((client) => {
          const clientMetadata = metadataMap.get(client.name);
          return {
            name: client.name,
            status: client.status,
            contract: resolveContract(clientMetadata, client.config) ?? '',
            partner: clientMetadata?.partner || '',
            accountOrg: orgMap.get(client.name) ?? clientMetadata?.accountOrg ?? '',
            trialStartDate: clientMetadata?.trialStartDate || '',
            trialEndDate: clientMetadata?.trialEndDate || '',
            metadataNotes: clientMetadata?.notes || '',
            inactive: flags.isInactive(client.name) ? 'yes' : '',
            doNotDeploy: flags.deployBlock(client.name)?.reason || '',
            deploymentCount: client.deploymentCount,
            clientAccountId: client.config.clientAccountId,
            region: client.config.region,
            devInstance: client.config.devInstance,
            customDomain: client.config.customDomain,
            qBusinessRegion: client.config.qBusinessRegion,
            provisionQResources: client.config.provisionQResources,
            allApps: client.config.allApps,
            allProdApps: client.config.allProdApps,
            apps: JSON.stringify(client.config.apps || {}),
            preferredKnowledgeBase: client.config.preferredKnowledgeBase,
            embeddingModel: client.config.embeddingModel,
            bedrockParserModel: client.config.bedrockParserModel,
            visionModelType: client.config.visionModelType,
            senderEmail: client.config.senderEmail,
            receiverEmails: JSON.stringify(client.config.receiverEmails || []),
            bedrockAccount: client.config.bedrockAccount,
            numaChatAgents: client.config.numaChatAgents,
            allowBedrockQuotaSharing: client.config.allowBedrockQuotaSharing,
            pipedreamIntegrations: client.config.pipedreamIntegrations,
            agents: client.config.agents,
            brandingProviderEnabled: client.config.brandingProviderEnabled,
            webCrawlerConfigs: JSON.stringify(client.config.webCrawlerConfigs || []),
            sharePointConfigs: JSON.stringify(client.config.sharePointConfigs || []),
            boxConfigs: JSON.stringify(client.config.boxConfigs || []),
            teamsConfigs: JSON.stringify(client.config.teamsConfigs || []),
            s3Configs: JSON.stringify(client.config.s3Configs || []),
            budget: JSON.stringify(client.config.budget || null),
          };
        });

        // Define columns for CSV export
        const columns = exportData.length > 0 ? (Object.keys(exportData[0]) as (keyof (typeof exportData)[0])[]) : [];
        const csv = FileExportService.arrayToCSV(exportData, columns);
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        const suffix = withDefaults ? '-with-defaults' : '-raw';
        const filename = `client-configs${suffix}-${timestamp}.csv`;

        FileExportService.downloadFile({
          name: filename,
          content: csv,
          mimeType: 'text/csv',
          size: new Blob([csv]).size,
        });
      } else {
        // JSON export
        const exportData = enrichedClients.map((client) => ({
          name: client.name,
          status: client.status,
          deploymentCount: client.deploymentCount,
          accountOrg: orgMap.get(client.name) ?? metadataMap.get(client.name)?.accountOrg ?? null,
          config: client.config,
          metadata: metadataMap.get(client.name) || null,
          lastDeployment: client.lastDeployment,
        }));

        const json = JSON.stringify(exportData, null, 2);
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        const suffix = withDefaults ? '-with-defaults' : '-raw';
        const filename = `client-configs${suffix}-${timestamp}.json`;

        FileExportService.downloadFile({
          name: filename,
          content: json,
          mimeType: 'application/json',
          size: new Blob([json]).size,
        });
      }
    } catch (error) {
      console.error('Export failed:', error);
      setError('The export failed and no file was made. Try again.');
    } finally {
      setExporting(false);
    }
  };

  // Modal trigger handlers
  const handleExportCSV = () => {
    setExportFormat('csv');
    setShowExportModal(true);
  };

  const handleExportJSON = () => {
    setExportFormat('json');
    setShowExportModal(true);
  };

  const isFiltered = Boolean(search.trim()) || activeFilterCount > 0;
  const inScope = scopeCounts[scope];
  const countText = isFiltered ? `${filtered.length} of ${inScope} match` : plural(inScope, 'client', 'clients');

  const scopeWord = scope === 'inactive' ? 'inactive' : 'active';
  const otherWord = scope === 'inactive' ? 'active' : 'inactive';
  const emptyContent =
    scope !== 'all' && isFiltered && hiddenMatches > 0 ? (
      <>
        No {scopeWord} client matches. {plural(hiddenMatches, `${otherWord} client does`, `${otherWord} clients do`)}.
        <Button variant="link" className="configs-list__search-all" onClick={() => setScope('all')}>
          Search all
        </Button>
      </>
    ) : scope === 'inactive' && !isFiltered ? (
      'No client is marked inactive.'
    ) : undefined;

  // ── The open client's facts ──
  const meta = selected ? metadataMap.get(selected.name) : undefined;
  const liveOrg = selected ? orgMap.get(selected.name) : undefined;
  const orgInfo = getAccountOrgBadgeInfo(liveOrg ?? meta?.accountOrg ?? null);
  const knownPartners = partnerNames(metadataMap.values());
  const configJson = selected ? JSON.stringify(selected.config, null, 2) : '';
  const metaJson = meta ? JSON.stringify(meta, null, 2) : '';

  return (
    <div className="configs-page">
      <PageHeader
        title="Client configs"
        actions={
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" disabled={loading || exporting || clients.length === 0}>
              {exporting ? <Spinner animation="border" size="sm" className="me-2" /> : <Download className="me-1" />}
              {exporting ? 'Exporting' : 'Export'}
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <Dropdown.Item onClick={handleExportCSV}>Export as CSV</Dropdown.Item>
              <Dropdown.Item onClick={handleExportJSON}>Export as JSON</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        }
      />

      {/* ── Search and every filter, in one row ── */}
      <div className="configs-toolbar">
        <Nav
          variant="pills"
          className="nav-pills-custom configs-toolbar__scope"
          activeKey={scope}
          onSelect={(key) => key && setScope(key as Scope)}
          aria-label="Which clients to list"
        >
          <Nav.Item>
            <Nav.Link eventKey="active">
              Active<span className="configs-toolbar__scope-count">{scopeCounts.active}</span>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="inactive">
              Inactive<span className="configs-toolbar__scope-count">{scopeCounts.inactive}</span>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="all">
              All<span className="configs-toolbar__scope-count">{scopeCounts.all}</span>
            </Nav.Link>
          </Nav.Item>
        </Nav>
        <InputGroup className="configs-toolbar__search">
          <InputGroup.Text>
            <Search />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Search clients"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search clients"
          />
        </InputGroup>
        <Form.Select
          className="configs-toolbar__select"
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value)}
          aria-label="Filter by organisation"
        >
          <option value="">Any organisation</option>
          {ACCOUNT_ORG_VALUES.map((o) => (
            <option key={o} value={o}>
              {ACCOUNT_ORG_DISPLAY[o].label}
            </option>
          ))}
          <option value="unknown">Organisation not known</option>
        </Form.Select>
        <Form.Select
          className="configs-toolbar__select"
          value={contractFilter}
          onChange={(e) => setContractFilter(e.target.value)}
          aria-label="Filter by contract"
        >
          <option value="">Any contract</option>
          {CONTRACT_VALUES.map((c) => (
            <option key={c} value={c}>
              {CONTRACT_DISPLAY[c].label}
            </option>
          ))}
          <option value="expired">Trial expired</option>
          {knownPartners.map((name) => (
            <option key={name} value={`partner:${name}`}>
              Partner: {name}
            </option>
          ))}
          <option value="unset">No contract set</option>
        </Form.Select>
        <Button
          variant="outline-secondary"
          className={dateFilterCount > 0 ? 'configs-toolbar__dates is-on' : 'configs-toolbar__dates'}
          aria-expanded={showDates}
          aria-controls="configs-dates"
          onClick={() => setShowDates(!showDates)}
        >
          Trial dates
          {dateFilterCount > 0 && <span className="configs-toolbar__dates-count">{dateFilterCount}</span>}
        </Button>
        {activeFilterCount > 0 && (
          <Button variant="link" className="configs-toolbar__clear" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
        {!loading && <span className="configs-toolbar__count">{countText}</span>}
      </div>
      <Collapse in={showDates}>
        <div id="configs-dates">
          <div className="configs-dates">
            <div>
              <Form.Label htmlFor="configs-trial-start-from">Trial started on or after</Form.Label>
              <Form.Control
                id="configs-trial-start-from"
                type="date"
                value={trialStartFrom}
                max={trialStartTo || undefined}
                onChange={(e) => setTrialStartFrom(e.target.value)}
              />
            </div>
            <div>
              <Form.Label htmlFor="configs-trial-start-to">Trial started on or before</Form.Label>
              <Form.Control
                id="configs-trial-start-to"
                type="date"
                value={trialStartTo}
                min={trialStartFrom || undefined}
                onChange={(e) => setTrialStartTo(e.target.value)}
              />
            </div>
            <div>
              <Form.Label htmlFor="configs-trial-end-from">Trial ends on or after</Form.Label>
              <Form.Control
                id="configs-trial-end-from"
                type="date"
                value={trialEndFrom}
                max={trialEndTo || undefined}
                onChange={(e) => setTrialEndFrom(e.target.value)}
              />
            </div>
            <div>
              <Form.Label htmlFor="configs-trial-end-to">Trial ends on or before</Form.Label>
              <Form.Control
                id="configs-trial-end-to"
                type="date"
                value={trialEndTo}
                min={trialEndFrom || undefined}
                onChange={(e) => setTrialEndTo(e.target.value)}
              />
            </div>
          </div>
        </div>
      </Collapse>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {requestedMissing && (
        <Alert variant="warning">
          There is no client named <span className="mono">{requested}</span>. It may be deleted. The first client in the
          list is open in its place.
        </Alert>
      )}
      {flags.status === 'error' && (
        <Alert variant="warning">
          The clients' saved records could not be read, so statuses and tags can be missing or out of date.{' '}
          {flags.error}{' '}
          <Button variant="link" className="p-0 align-baseline" onClick={() => void flags.reload()}>
            Try again
          </Button>
        </Alert>
      )}
      <OrganisationReview clients={clients} orgMap={orgMap} />

      <div className="configs-panes">
        {/* ── The clients ── */}
        <Card className="configs-list">
          {/* ── The change for the ticked rows. It shows only while a row is ticked. ── */}
          {tickedListed.length > 0 && (
            <div className="configs-selection" role="toolbar" aria-label="Change the ticked clients">
              <span className="configs-selection__count">{tickedListed.length.toLocaleString()} selected</span>
              <Button
                variant="link"
                size="sm"
                className="configs-selection__clear"
                onClick={() => setTicked(new Set())}
              >
                Clear
              </Button>
              <span className="configs-selection__rule" aria-hidden="true" />
              {tickedActive.length > 0 && (
                <Button size="sm" variant="outline-secondary" onClick={() => setBulkAction('mark-inactive')}>
                  Mark {tickedActive.length.toLocaleString()} inactive
                </Button>
              )}
              {tickedInactive.length > 0 && (
                <Button size="sm" variant="outline-secondary" onClick={() => setBulkAction('mark-active')}>
                  Mark {tickedInactive.length.toLocaleString()} active again
                </Button>
              )}
            </div>
          )}
          <div className="configs-list__scroll">
            <ClientTableGroup
              clients={filtered}
              loading={loading}
              selectedClient={selected}
              onSelectClient={selectClient}
              onUpdateClient={editClient}
              searchTerm={search}
              metadataMap={metadataMap}
              orgMap={orgMap}
              tickedNames={ticked}
              onTickedChange={changeTicks}
              emptyContent={emptyContent}
            />
          </div>
        </Card>

        {/* ── The open client ── */}
        <Card className="configs-detail">
          {loading && (
            <div className="configs-detail__empty">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading clients
            </div>
          )}
          {!loading && !selected && (
            <div className="configs-detail__empty">
              {error ? 'No config to show.' : 'There are no clients yet. Create one from Tools.'}
            </div>
          )}
          {selected && (
            <>
              <div className="configs-detail__head">
                <div className="configs-detail__title-row">
                  <h2 className="configs-detail__name">{selected.name}</h2>
                  <ClientTags clientName={selected.name} flags={flags} config={selected.config} />
                  <Button
                    size="sm"
                    variant="primary"
                    className="configs-detail__edit"
                    onClick={() => editClient(selected)}
                  >
                    <Pencil size={12} className="me-1" />
                    Edit config
                  </Button>
                </div>
                <dl className="configs-facts">
                  <div>
                    <dt>Type</dt>
                    <dd>{selected.config.devInstance === true ? 'Dev or demo' : 'Customer'}</dd>
                  </div>
                  <div>
                    <dt>Region</dt>
                    <dd className="mono">{selected.config.region}</dd>
                  </div>
                  <div>
                    <dt>Organisation</dt>
                    <dd>
                      {orgInfo ? orgInfo.label : 'Not known'}
                      <span className="configs-facts__sub">
                        {liveOrg !== undefined ? 'Checked against AWS just now' : 'From the saved record'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>AWS account</dt>
                    <dd>
                      {selected.config.clientAccountId ? (
                        <>
                          <span className="mono">{selected.config.clientAccountId}</span>
                          <CopyButton value={selected.config.clientAccountId} label="Copy account id" />
                        </>
                      ) : (
                        'None set'
                      )}
                    </dd>
                  </div>
                  <div className="configs-facts__flag">
                    <dt>Deploys</dt>
                    <dd>
                      <ClientFlagControl kind="deploy-block" clientName={selected.name} />
                    </dd>
                  </div>
                  <div className="configs-facts__flag">
                    <dt>State</dt>
                    <dd>
                      <ClientFlagControl kind="inactive" clientName={selected.name} />
                    </dd>
                  </div>
                  {meta && (meta.trialStartDate || meta.trialEndDate) && (
                    <div>
                      <dt>Trial</dt>
                      <dd>
                        {meta.trialStartDate ? formatNzDate(meta.trialStartDate) : 'No start date'} to{' '}
                        {meta.trialEndDate ? formatNzDate(meta.trialEndDate) : 'no end date'}
                      </dd>
                    </div>
                  )}
                  {meta?.notes && (
                    <div className="configs-facts__wide">
                      <dt>Notes</dt>
                      <dd>{meta.notes}</dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="configs-detail__scroll">
                <section className="configs-json">
                  <div className="configs-json__head">
                    <h3 className="section-label">Config</h3>
                    <CopyJsonButton value={configJson} label="Copy JSON" />
                  </div>
                  <pre className="configs-json__code">{configJson}</pre>
                </section>

                {meta && (
                  <section className="configs-json">
                    <div className="configs-json__head">
                      <h3 className="section-label">
                        Client metadata
                        {meta.updatedAt && (
                          <span className="configs-json__when" title={formatNzDateTime(meta.updatedAt)}>
                            changed {timeAgo(meta.updatedAt)}
                            {meta.updatedBy ? ` by ${meta.updatedBy.split('@')[0]}` : ''}
                          </span>
                        )}
                      </h3>
                      <CopyJsonButton value={metaJson} label="Copy JSON" />
                    </div>
                    <p className="configs-json__note">
                      Kept by the portal and never sent to the client: its status, trial dates, notes and tags.
                    </p>
                    <pre className="configs-json__code">{metaJson}</pre>
                  </section>
                )}
              </div>
            </>
          )}
        </Card>
      </div>

      <BulkInactiveDialog
        action={bulkAction}
        clientNames={bulkAction === 'mark-active' ? tickedInactive : tickedActive}
        onClose={() => setBulkAction(null)}
        onDone={(changed) => changeTicks(changed, false)}
      />

      {/* ── Export: one question before the file is made ── */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Export {filtered.length} {filtered.length === 1 ? 'client' : 'clients'} as {exportFormat.toUpperCase()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="configs-export__intro">
            The file holds the clients in the list now, each with its config, its metadata and its newest deploy. Choose
            what to write for a setting a client has not set.
          </p>
          <div className="configs-export__choices">
            <Button
              variant="outline-secondary"
              className="configs-export__choice"
              onClick={() => {
                setShowExportModal(false);
                performExport(exportFormat, false);
              }}
            >
              <span className="configs-export__choice-title">Only what is set</span>
              <span className="configs-export__choice-note">A setting the client has not set stays empty.</span>
            </Button>
            <Button
              variant="primary"
              className="configs-export__choice"
              onClick={() => {
                setShowExportModal(false);
                performExport(exportFormat, true);
              }}
            >
              <span className="configs-export__choice-title">Fill in the defaults</span>
              <span className="configs-export__choice-note">A setting the client has not set gets its default.</span>
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
